/**
 * Tutor personal-account form section.
 * Identity / contact (create vs edit), professional situation, address.
 */
import * as React from 'react';
import { IdCard, KeyRound, Mail, User } from 'lucide-react';
import { Input } from '@/components/forms/Input';
import { Select } from '@/components/forms/Select';
import { SmartContactInput } from '@/components/forms/SmartContactInput';
import type { StoredContact } from '@/components/forms/SmartContactInput';
import { PhoneInput } from '@/components/forms/PhoneInput';
import { AddressAutocompleteInput } from '@/components/forms/AddressAutocompleteInput';
import { DateSplitInput } from '@/components/forms/DateSplitInput';
import { DepartmentSelect } from '@/components/common/DepartmentSelect';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { Button } from '@/components/forms/Button';
import { useEstablishmentsAutocomplete } from '@/hooks/useEstablishmentsAutocomplete';
import { getErrorMessage } from '@/utils/errors-utils';
import {
  applyInPersonTutorContactChange,
  getTutorMaxBirthDate,
  inPersonTutorAddressFromAutocomplete,
  isValuedEngagementRequired,
  mapAutocompleteIdLabelSelection,
  prependAutocompleteSeedOption,
} from '@/components/tutors/inperson/tutor-form-utils';
import { GENDER_OPTIONS } from '@/components/tutors/tutor-configs';
import {
  ASSOCIATIVE_ENGAGEMENT_TOOLTIP,
  inPersonProSituationSelectOptions,
  YES_NO_OPTIONS,
} from '@/constants/shared-catalog';
import {
  writeUnifiedContactSwap,
  canSwapPrimaryContact,
  contactExistsErrorMessage,
  oppositeContactType,
  primaryContactFieldError,
} from '@/utils/user-contact-utils';

/** Props for {@link ProSituationSubFields}. */
interface ProSituationSubFieldsProps {
  professionalSituation: string;
  establishmentId: string;
  establishmentName: string;
  onChange: (field: string, value: string) => void;
  showErrors?: boolean;
}

/** School establishment field shown for `student` and `high_school_student` situations. */
function ProSituationSubFields({
  professionalSituation,
  establishmentId,
  establishmentName,
  onChange,
  showErrors = false,
}: Readonly<ProSituationSubFieldsProps>) {
  const {
    options: establishmentOptions,
    isLoading: establishmentLoading,
    onSearch: handleEstablishmentSearch,
  } = useEstablishmentsAutocomplete();

  const enrichedOptions = React.useMemo(
    () => prependAutocompleteSeedOption(establishmentOptions, establishmentId, establishmentName),
    [establishmentOptions, establishmentId, establishmentName],
  );

  /** Persist establishment id + label into the parent form. */
  function handleEstabSelect(value: string): void {
    const selected = mapAutocompleteIdLabelSelection(value, enrichedOptions);
    onChange('proSituationEstablishmentId', selected.id);
    onChange('proSituationEstablishmentName', selected.label);
  }

  if (professionalSituation === 'student' || professionalSituation === 'high_school_student') {
    return (
      <Autocomplete
        label="Nom de l'établissement"
        required
        error={
          showErrors && !establishmentId ? getErrorMessage('requiredEstablishmentName') : undefined
        }
        placeholder="Rechercher un établissement..."
        options={enrichedOptions}
        value={establishmentId || ''}
        onChange={handleEstabSelect}
        onSearch={handleEstablishmentSearch}
        isLoading={establishmentLoading}
        minSearchLength={2}
        inputSize="sm"
      />
    );
  }

  return null;
}

/** Server contact fields used by the edit-mode dirty check. */
type ServerUserContact = {
  email?: string | null;
  phone?: string | null;
  primaryContactKind?: 'email' | 'phone' | null;
};

/** Read user contact from the in-person tutor full-state payload. */
function serverContactFromFullState(fullState: unknown): ServerUserContact {
  const user = (fullState as { record?: { user?: ServerUserContact } } | undefined)?.record?.user;
  return {
    email: user?.email,
    phone: user?.phone,
    primaryContactKind: user?.primaryContactKind,
  };
}

/** Props for {@link TutorPersonalAccountSection}. */
interface Props {
  mode: 'create' | 'edit';
  formData: Record<string, any>;
  fullState: unknown;
  defaultCountry: string;
  set: (field: string, value: any) => void;
  submitAttempted: boolean;
  /** Edit mode only: userId for action buttons */
  userId?: string;
  /** Edit mode only: emailVerified status */
  emailVerified?: boolean;
  /** Contact exists check result */
  contactExists?: boolean | null;
  /** Secondary contact exists check result (create mode only) */
  secondaryContactExists?: boolean | null;
  /** Edit mode: primary contact exists */
  primaryContactExists?: boolean | null;
  /** Edit mode: primary and secondary are identical */
  contactsAreIdentical?: boolean;
  /** Form validator — format errors + touch, same as remote tutor. */
  validation?: {
    getFieldError: (field: string) => string | undefined;
    getFieldState?: (field: string) => 'default' | 'error' | 'success' | 'warning' | undefined;
    markAsTouched?: (field: string) => void;
  };
}

/** Render establishment sub-field for `student` and `high_school_student` situations. */
function renderProSituationSubFields(params: {
  professionalSituation: string | undefined;
  establishmentId: string;
  establishmentName: string;
  set: (field: string, value: string) => void;
  submitAttempted: boolean;
}): React.ReactNode {
  if (
    params.professionalSituation !== 'student' &&
    params.professionalSituation !== 'high_school_student'
  ) {
    return null;
  }
  return (
    <ProSituationSubFields
      professionalSituation={params.professionalSituation}
      establishmentId={params.establishmentId}
      establishmentName={params.establishmentName}
      onChange={params.set}
      showErrors={params.submitAttempted}
    />
  );
}

/** Tutor personal-account form section. */
export function TutorPersonalAccountSection({
  mode,
  formData,
  fullState,
  defaultCountry,
  set,
  submitAttempted,
  userId,
  emailVerified,
  contactExists,
  secondaryContactExists,
  primaryContactExists,
  contactsAreIdentical,
  validation,
}: Readonly<Props>) {
  const isEdit = mode === 'edit';
  const effectivePrimaryExists = isEdit ? primaryContactExists : contactExists;
  const contactExistsError = primaryContactFieldError(
    effectivePrimaryExists,
    formData.contactType,
    !!contactsAreIdentical,
  );
  const secondaryContactType = oppositeContactType(formData.contactType);
  const secondaryContactExistsError = contactExistsErrorMessage(
    isEdit ? contactExists : secondaryContactExists,
    secondaryContactType,
  );
  const contactFormatError = validation?.getFieldError('contact');
  const secondaryFormatError = validation?.getFieldError('secondaryContact');
  const contactFieldError = contactExistsError ?? contactFormatError;
  const secondaryFieldError = secondaryContactExistsError ?? secondaryFormatError;

  /** Mark secondary contact touched so format errors can show. */
  function handleSecondaryBlur(): void {
    validation?.markAsTouched?.('secondaryContact');
  }

  /** Reset sub-fields and update professional situation on select change. */
  function handleProSituationChange(val: string): void {
    set('professionalSituation', val);
    set('proSituationEstablishmentId', '');
    set('proSituationEstablishmentName', '');
    set('proSituationAssociativeEngagement', '');
  }

  /** Persist the associative-engagement answer. */
  function handleAssociativeEngagementChange(value: string): void {
    set('proSituationAssociativeEngagement', value);
  }

  /** Catalog error after submit when valued engagement is required and still empty. */
  function valuedEngagementFieldError(): string | undefined {
    if (
      !submitAttempted ||
      !isValuedEngagementRequired(formData.professionalSituation) ||
      formData.proSituationAssociativeEngagement
    ) {
      return undefined;
    }
    return getErrorMessage('valuedEngagementRequired');
  }

  const proSituationOptions = inPersonProSituationSelectOptions(formData.professionalSituation);
  const serverContact = serverContactFromFullState(fullState);

  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <IdCard className="w-4 h-4" />
        Compte et informations personnelles
      </h4>

      <div className="form-section">
        <h4 className="form-section-title">
          <KeyRound className="w-4 h-4" />
          Compte
        </h4>
        {renderInPersonTutorCreateAccount({
          mode,
          formData,
          defaultCountry,
          set,
          contactFieldError,
          secondaryFieldError,
          onSecondaryBlur: handleSecondaryBlur,
        })}
        {renderInPersonTutorEditAccount({
          mode,
          formData,
          defaultCountry,
          serverEmail: serverContact.email,
          serverPhone: serverContact.phone,
          serverPrimaryKind: serverContact.primaryContactKind,
          set,
          contactFieldError,
          secondaryFieldError,
          onSecondaryBlur: handleSecondaryBlur,
          userId,
          emailVerified,
        })}
      </div>

      <div className="form-section">
        <h4 className="form-section-title">
          <User className="w-4 h-4" />
          Informations personnelles
        </h4>
        <DateSplitInput
          label="Date de naissance"
          value={formData.dateOfBirth ?? ''}
          onChange={(value) => set('dateOfBirth', value)}
          onBlur={() => validation?.markAsTouched?.('dateOfBirth')}
          maxDate={getTutorMaxBirthDate()}
          error={validation?.getFieldError('dateOfBirth')}
          state={validation?.getFieldState?.('dateOfBirth')}
          size="sm"
        />
        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Situation professionnelle"
            placeholder="Sélectionner"
            value={formData.professionalSituation ?? ''}
            onChange={handleProSituationChange}
            options={proSituationOptions}
            size="sm"
            required
          />
          {renderProSituationSubFields({
            professionalSituation: formData.professionalSituation,
            establishmentId: formData.proSituationEstablishmentId ?? '',
            establishmentName: formData.proSituationEstablishmentName ?? '',
            set,
            submitAttempted,
          })}
          {formData.professionalSituation ? (
            <Select
              label="Engagement associatif valorisé"
              labelTooltip={ASSOCIATIVE_ENGAGEMENT_TOOLTIP}
              placeholder="Sélectionner"
              value={formData.proSituationAssociativeEngagement ?? ''}
              onChange={handleAssociativeEngagementChange}
              options={YES_NO_OPTIONS}
              size="sm"
              required={isValuedEngagementRequired(formData.professionalSituation)}
              error={valuedEngagementFieldError()}
            />
          ) : null}
        </div>
        <AddressAutocompleteInput
          label="Adresse"
          value={formData.address ?? ''}
          onChange={(value) => set('address', value)}
          onSelection={(result) => {
            Object.entries(inPersonTutorAddressFromAutocomplete(result)).forEach(([key, val]) =>
              set(key, val),
            );
          }}
          size="sm"
          required
        />
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Code postal"
            placeholder="Ex: 75001"
            value={formData.zipCode ?? ''}
            onChange={(e) => set('zipCode', e.target.value)}
            inputSize="sm"
            required
          />
          <Input
            label="Ville"
            placeholder="Ex: Paris"
            value={formData.city ?? ''}
            onChange={(e) => set('city', e.target.value)}
            inputSize="sm"
            required
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Pays"
            placeholder="Ex: France"
            value={formData.country ?? ''}
            onChange={(e) => set('country', e.target.value)}
            inputSize="sm"
          />
          <DepartmentSelect
            label="Département de résidence"
            value={formData.department ?? ''}
            onChange={(val) => set('department', val)}
            placeholder="Sélectionner"
            inputSize="sm"
            required
          />
        </div>
      </div>
    </div>
  );
}

/** Optional secondary email or phone field. */
function renderInPersonTutorSecondaryContact(params: {
  contactType: string | undefined;
  secondaryContact: string;
  countryCode: string;
  fieldError: string | undefined;
  set: (field: string, value: unknown) => void;
  onBlur?: () => void;
}): React.ReactNode {
  if (!params.contactType || params.contactType === 'unknown') return null;
  /** Persist secondary contact value. */
  function handleChange(event: React.ChangeEvent<HTMLInputElement>): void {
    params.set('secondaryContact', event.target.value);
  }
  /** Persist secondary-phone country ISO. */
  function handleCountryChange(country: { code: string }): void {
    params.set('secondaryContactCountryCode', country.code);
  }
  if (params.contactType === 'email') {
    return (
      <div className="transition-all duration-300 animate-in fade-in slide-in-from-top-2">
        <PhoneInput
          label="Téléphone (contact secondaire, optionnel)"
          value={params.secondaryContact}
          onChange={handleChange}
          onBlur={params.onBlur}
          countryCode={params.countryCode}
          onCountryChange={handleCountryChange}
          inputSize="sm"
          state={params.fieldError ? 'error' : 'default'}
          error={params.fieldError}
        />
      </div>
    );
  }
  return (
    <div className="transition-all duration-300 animate-in fade-in slide-in-from-top-2">
      <Input
        type="text"
        label="Email (contact secondaire, optionnel)"
        value={params.secondaryContact}
        onChange={handleChange}
        onBlur={params.onBlur}
        icon={Mail}
        placeholder="Ex: contact@example.com"
        inputSize="sm"
        state={params.fieldError ? 'error' : 'default'}
        error={params.fieldError}
      />
    </div>
  );
}

/** Create-mode identity and contact fields. */
function renderInPersonTutorCreateAccount(params: {
  mode: 'create' | 'edit';
  formData: Record<string, any>;
  defaultCountry: string;
  set: (field: string, value: unknown) => void;
  contactFieldError: string | undefined;
  secondaryFieldError: string | undefined;
  onSecondaryBlur: () => void;
}): React.ReactNode {
  if (params.mode !== 'create') return null;
  /** Persist primary contact and inferred type on create. */
  function handleContactChange(value: string, type: string, countryCode?: string): void {
    applyInPersonTutorContactChange(
      params.set,
      params.formData.contactType,
      value,
      type,
      countryCode,
    );
  }
  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Input
          label="Prénom"
          placeholder="Ex: Marie"
          value={params.formData.firstName ?? ''}
          onChange={(e) => params.set('firstName', e.target.value)}
          inputSize="sm"
          required
        />
        <Input
          label="Nom"
          placeholder="Ex: Martin"
          value={params.formData.lastName ?? ''}
          onChange={(e) => params.set('lastName', e.target.value)}
          inputSize="sm"
          required
        />
        <Select
          label="Genre"
          placeholder="Sélectionner"
          value={params.formData.gender ?? ''}
          onChange={(val) => params.set('gender', val)}
          options={GENDER_OPTIONS}
          size="sm"
          required
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 mt-4">
        <SmartContactInput
          label="Contact principal"
          value={params.formData.contact ?? ''}
          onContactChange={handleContactChange}
          required
          inputSize="sm"
          state={params.contactFieldError ? 'error' : undefined}
          error={params.contactFieldError}
        />
        {renderInPersonTutorSecondaryContact({
          contactType: params.formData.contactType,
          secondaryContact: params.formData.secondaryContact ?? '',
          countryCode: params.formData.secondaryContactCountryCode ?? params.defaultCountry,
          fieldError: params.secondaryFieldError,
          set: params.set,
          onBlur: params.onSecondaryBlur,
        })}
      </div>
    </>
  );
}

/** Edit-mode identity and contact fields. */
function renderInPersonTutorEditAccount(params: {
  mode: 'create' | 'edit';
  formData: Record<string, any>;
  defaultCountry: string;
  serverEmail?: string | null;
  serverPhone?: string | null;
  serverPrimaryKind?: 'email' | 'phone' | null;
  set: (field: string, value: unknown) => void;
  contactFieldError: string | undefined;
  secondaryFieldError: string | undefined;
  onSecondaryBlur: () => void;
  userId?: string;
  emailVerified?: boolean;
}): React.ReactNode {
  if (params.mode !== 'edit') return null;

  /** Persist primary contact and inferred type on edit. */
  function handleContactChange(value: string, type: string, countryCode?: string): void {
    params.set('contact', value);
    params.set('contactType', type);
    if (countryCode) params.set('contactCountryCode', countryCode);
  }

  /** Swap primary and secondary contact fields. */
  function handleSwap(): void {
    writeUnifiedContactSwap(params.set, params.formData, params.defaultCountry);
  }

  /** Swap beside the primary field only while the primary is a real email or phone. */
  const swapButton = canSwapPrimaryContact(params.formData.contactType) ? (
    <Button type="button" preset="contact-swap" size="md" onClick={handleSwap} />
  ) : undefined;

  /** storedContact with live primaryKind so the field mode syncs after a swap. */
  const storedContact: StoredContact = {
    primaryKind:
      params.formData.contactType === 'email' || params.formData.contactType === 'phone'
        ? params.formData.contactType
        : null,
    serverPrimaryKind: params.serverPrimaryKind,
    email: params.serverEmail,
    phone: params.serverPhone,
  };

  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Input
          label="Prénom"
          placeholder="Ex: Marie"
          value={params.formData.firstName ?? ''}
          onChange={(e) => params.set('firstName', e.target.value)}
          inputSize="sm"
        />
        <Input
          label="Nom"
          placeholder="Ex: Martin"
          value={params.formData.lastName ?? ''}
          onChange={(e) => params.set('lastName', e.target.value)}
          inputSize="sm"
        />
        <Select
          label="Genre"
          placeholder="Sélectionner"
          value={params.formData.gender ?? ''}
          onChange={(val) => params.set('gender', val)}
          options={GENDER_OPTIONS}
          size="sm"
          required
        />
      </div>
      <div className="grid grid-cols-2 items-start gap-2 mt-4">
        <SmartContactInput
          label="Contact principal"
          value={params.formData.contact ?? ''}
          onContactChange={handleContactChange}
          storedContact={storedContact}
          inputSize="sm"
          showContactActions
          emailVerified={params.emailVerified}
          userId={params.userId}
          state={params.contactFieldError ? 'error' : undefined}
          error={params.contactFieldError}
          swapButton={swapButton}
        />
        {renderInPersonTutorSecondaryContact({
          contactType: params.formData.contactType || 'email',
          secondaryContact: params.formData.secondaryContact || '',
          countryCode: params.formData.secondaryContactCountryCode || params.defaultCountry,
          fieldError: params.secondaryFieldError,
          set: params.set,
          onBlur: params.onSecondaryBlur,
        })}
      </div>
    </>
  );
}
